/** Arcade's live sessions: the create call, the socket's framing and events, and the words. */

import { arcadeOrigin } from '@/config/runtime-config';

import { V1Error } from './api';

export const LIVE_RATE = 16_000;
export const FRAME_MS = 50;
export const LIVE_TRACK = '0';
export const CREDITS_PER_LIVE_MINUTE = 2;

export interface StreamTicket {
  url: string;
  expires_at: string;
}

/** What Arcade forwards from Stylus; every event carries the credits still available. */
export type LiveEvent = { credits_remaining?: number; seconds_remaining?: number } & (
  | { type: 'waiting' }
  | {
      type: 'ready';
      tracks: { track_id: string; resume_from: number; audio_ms: number }[];
      idle_seconds?: number;
      max_seconds?: number;
    }
  | { type: 'ack'; track_id: string; seq: number }
  | { type: 'partial'; track_id: string; text: string; start_ms: number; end_ms: number }
  | {
      type: 'final';
      id: string;
      track_id: string;
      text: string;
      start_ms: number;
      end_ms: number;
    }
  | { type: 'activity'; track_id: string; event: string; at_ms: number }
  | { type: 'notice'; code: string; message: string }
  | {
      type: 'ended';
      reason: string;
      duration_ms: number | null;
      status: string;
      verdict: string;
    }
  | { type: 'error'; code: string; message: string }
);

/** Close codes on which a reconnect cannot help: the session refused, or the ticket was bad. */
export const FINAL_CLOSE_CODES = new Set([4400, 4401]);

async function v1(path: string, secret: string, body?: unknown): Promise<unknown> {
  const response = await fetch(`${arcadeOrigin()}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${secret}`, 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await response.text();
  if (!response.ok) {
    let code: string | undefined;
    let message = `HTTP ${response.status}`;
    try {
      const parsed = JSON.parse(text) as { error?: { code?: string; message?: string } };
      code = parsed.error?.code;
      message = parsed.error?.message ?? message;
    } catch {
      // Not JSON; the status says enough.
    }
    throw new V1Error(response.status, code, message);
  }
  return text ? JSON.parse(text) : null;
}

/** `POST /v1/jobs` in live mode; the job id and its first single-use stream URL. */
export async function createLiveJob(
  secret: string,
  locale: string,
): Promise<{ id: string; stream: StreamTicket }> {
  const job = (await v1('/v1/jobs', secret, {
    mode: 'live',
    codec: 'linear16',
    sample_rate: LIVE_RATE,
    locale,
  })) as { id: string; stream: StreamTicket | null };
  if (!job.stream) {
    throw new V1Error(0, undefined, 'The live session came back without a stream.');
  }
  return { id: job.id, stream: job.stream };
}

/** A fresh ticket for reconnecting to a session whose socket dropped. */
export async function mintStreamTicket(jobId: string, secret: string): Promise<StreamTicket> {
  return (await v1(`/v1/jobs/${jobId}/stream-ticket`, secret)) as StreamTicket;
}

/** Ends a session from outside its socket, e.g. once the socket is gone. */
export async function endLiveSession(jobId: string, secret: string): Promise<void> {
  await v1(`/v1/jobs/${jobId}/end`, secret);
}

/** `[seq u32][pts u32][track id length u8][track id][PCM s16le]`, the header big-endian. */
export function encodeFrame(seq: number, samples: ArrayLike<number>): ArrayBuffer {
  const id = new TextEncoder().encode(LIVE_TRACK);
  const buffer = new ArrayBuffer(9 + id.length + samples.length * 2);
  const view = new DataView(buffer);
  view.setUint32(0, seq);
  view.setUint32(4, seq * FRAME_MS);
  view.setUint8(8, id.length);
  new Uint8Array(buffer, 9, id.length).set(id);
  for (let i = 0; i < samples.length; i++) {
    const v = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(9 + id.length + i * 2, v < 0 ? v * 0x8000 : v * 0x7fff, true);
  }
  return buffer;
}

/** Linear resampling to `LIVE_RATE`, cut into whole frames; keeps the remainder between calls. */
export function makeResampler(inputRate: number): (input: Float32Array) => Float32Array[] {
  const perFrame = (LIVE_RATE * FRAME_MS) / 1000;
  const step = inputRate / LIVE_RATE;
  let position = 0;
  let carry = new Float32Array(0);
  let out: number[] = [];
  return (input) => {
    const buffer = new Float32Array(carry.length + input.length);
    buffer.set(carry);
    buffer.set(input, carry.length);
    while (position + 1 < buffer.length) {
      const i = Math.floor(position);
      const f = position - i;
      out.push(buffer[i] * (1 - f) + buffer[i + 1] * f);
      position += step;
    }
    const keep = Math.floor(position);
    carry = buffer.slice(keep);
    position -= keep;
    const frames: Float32Array[] = [];
    while (out.length >= perFrame) {
      frames.push(Float32Array.from(out.slice(0, perFrame)));
      out = out.slice(perFrame);
    }
    return frames;
  };
}

export interface LiveLine {
  id: string;
  text: string;
  startMs: number;
}

export interface LiveWords {
  finals: LiveLine[];
  /** The words of the turn still being spoken; replaced by each partial. */
  partial: string;
}

export const NO_WORDS: LiveWords = { finals: [], partial: '' };

/** Folds a partial or final into what is on screen; a final replaces the partial it settles. */
export function applyWords(words: LiveWords, event: LiveEvent): LiveWords {
  if (event.type === 'partial') {
    return { ...words, partial: event.text };
  }
  if (event.type === 'final') {
    if (words.finals.some((line) => line.id === event.id) || !event.text.trim()) {
      return { ...words, partial: '' };
    }
    return {
      finals: [...words.finals, { id: event.id, text: event.text, startMs: event.start_ms }],
      partial: '',
    };
  }
  return words;
}

/** What a live session's refusal or failure means to the person recording. */
export function describeLiveError(code: string | undefined, message: string): string {
  switch (code) {
    case 'INSUFFICIENT_CREDITS':
      return `A live session needs at least ${CREDITS_PER_LIVE_MINUTE} credits, and this month’s are used up.`;
    case 'TOO_MANY_IN_FLIGHT':
      return 'Another job is still running. The playground takes one job at a time on your account — try again when it finishes.';
    case 'LIVE_CAPACITY':
      return 'Every live session is in use right now. Try again in a moment; this cost nothing.';
    case 'SESSION_NOT_READY':
      return 'The session took too long to start. Try again.';
    case 'SESSION_EXPIRED':
      return 'The session expired before any audio arrived. Start another.';
    default:
      return message || 'The live session stopped unexpectedly.';
  }
}
